import React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LandscapeIcon from '@mui/icons-material/Landscape';
import LoopIcon from '@mui/icons-material/Loop';
import InsightsIcon from '@mui/icons-material/Insights';
import BugReportIcon from '@mui/icons-material/BugReport';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GroupsIcon from '@mui/icons-material/Groups';
import { useSession } from '../../context/SessionContext';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import WorkspaceSwitcher from '../workspace/WorkspaceSwitcher';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#0a0a0a',  // Match marketing pages dark background
  color: '#FFFFFF',
  boxShadow: 'none',
  borderBottom: '1px solid #2a2a2a',
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#e5e5e5',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& .logo-icon': {
    color: '#3b82f6',
  }
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#a3a3a3',
  fontWeight: 500,
  textTransform: 'none',
  padding: theme.spacing(1, 2),
  borderRadius: 6,
  '&:hover': {
    backgroundColor: '#1a1a1a',
    color: '#e5e5e5',
  },
  // The current page reads as a quiet tinted state — blue text on a faint blue
  // wash — rather than a solid fill, so it marks "you are here" without fighting
  // the solid-blue primary actions (e.g. "New list") for attention.
  '&.active': {
    backgroundColor: 'rgba(59, 130, 246, 0.12)',
    color: '#60a5fa',
  },
  '&.active:hover': {
    backgroundColor: 'rgba(59, 130, 246, 0.18)',
    color: '#60a5fa',
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
  color: 'white',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1, 3),
  borderRadius: 6,
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #3b82f6 20%, #8b5cf6 100%)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
}));

const SessionIndicator = styled(Chip)(({ theme }) => ({
  backgroundColor: '#1a1a1a',
  color: '#3b82f6',
  fontWeight: 500,
  border: '1px solid #2a2a2a',
  '& .MuiChip-icon': {
    color: '#3b82f6',
  },
}));

const Navbar = () => {
  const { session, participant } = useSession();
  const { isAuthenticated, user, logout, loading: authLoading } = useAuth();
  const { isShapeUpEnabled, isScrumEnabled, hasStakeholderViews, organizations } = useWorkspace();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const scrumEnabled = isScrumEnabled();
  const shapeUpEnabled = isShapeUpEnabled();
  // The Stakeholders link only appears once an admin has created a view, so it
  // never dead-ends into an empty page.
  const stakeholdersEnabled = shapeUpEnabled && hasStakeholderViews();
  const homePath = scrumEnabled ? '/dashboard' : '/shape-up';

  // One "Analytics" entry: Shape Up analytics takes the slot when Shape Up is on,
  // otherwise the estimation dashboard shows in scrum mode. Never both.
  const analyticsTo = shapeUpEnabled ? '/shape-up/analytics' : scrumEnabled ? '/analytics' : null;

  // The nav link matching the current page, decided by longest matching prefix so
  // a sub-route (e.g. /shape-up/analytics) lights up its own tab rather than the
  // parent "/shape-up" one. Feeds the NavButton .active styling below.
  const navPaths = [
    '/features',
    '/pricing',
    '/dashboard',
    '/create-session',
    shapeUpEnabled && '/shape-up',
    shapeUpEnabled && '/cycles',
    stakeholdersEnabled && '/shape-up/stakeholders',
    analyticsTo,
    '/scratchpad',
    '/admin',
  ].filter(Boolean);
  const activePath = navPaths
    .filter(p => pathname === p || pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0] || null;

  // The workspace switcher is only worth showing when there is something to
  // switch between. A single org needs no picker.
  const showWorkspaceSwitcher = organizations.length !== 1;

  const handleLogout = () => {
    logout();
    navigate('/');
    setAnchorEl(null);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <StyledAppBar position="fixed">
      <Container maxWidth="xl">
        <Toolbar sx={{ minHeight: '70px !important', py: 1 }}>
          {/* Logo Section */}
          <Logo
            component={RouterLink}
            to={isAuthenticated ? homePath : "/"}
            sx={{ textDecoration: 'none', flexGrow: { xs: 1, md: 0 }, mr: { md: 4 } }}
          >
            <BusinessIcon className="logo-icon" sx={{ fontSize: '2rem' }} />
            PlanningSync
          </Logo>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', flexGrow: 1 }}>
            {!authLoading && !isAuthenticated && (
              <>
                <NavButton component={RouterLink} to="/features" className={activePath === '/features' ? 'active' : undefined}>
                  Features
                </NavButton>
                <NavButton component={RouterLink} to="/pricing" className={activePath === '/pricing' ? 'active' : undefined}>
                  Pricing
                </NavButton>
              </>
            )}
            
            {isAuthenticated && (
              <>
                {showWorkspaceSwitcher && <WorkspaceSwitcher />}
                {scrumEnabled && (
                  <>
                    <NavButton
                      component={RouterLink}
                      to="/dashboard"
                      className={activePath === '/dashboard' ? 'active' : undefined}
                      startIcon={<DashboardIcon />}
                    >
                      Dashboard
                    </NavButton>
                    <NavButton
                      component={RouterLink}
                      to="/create-session"
                      className={activePath === '/create-session' ? 'active' : undefined}
                      startIcon={<CalendarTodayIcon />}
                    >
                      Meetings
                    </NavButton>
                  </>
                )}
                {shapeUpEnabled && (
                  <NavButton
                    component={RouterLink}
                    to="/shape-up"
                    className={activePath === '/shape-up' ? 'active' : undefined}
                    startIcon={<LandscapeIcon />}
                  >
                    Shape Up
                  </NavButton>
                )}
                {shapeUpEnabled && (
                  <NavButton
                    component={RouterLink}
                    to="/cycles"
                    className={activePath === '/cycles' ? 'active' : undefined}
                    startIcon={<LoopIcon />}
                  >
                    Cycles
                  </NavButton>
                )}
                {shapeUpEnabled && (
                  <NavButton
                    component={RouterLink}
                    to="/bugs"
                    className={activePath === '/bugs' ? 'active' : undefined}
                    startIcon={<BugReportIcon />}
                  >
                    Bugs
                  </NavButton>
                )}
                {stakeholdersEnabled && (
                  <NavButton
                    component={RouterLink}
                    to="/shape-up/stakeholders"
                    className={activePath === '/shape-up/stakeholders' ? 'active' : undefined}
                    startIcon={<GroupsIcon />}
                  >
                    Stakeholders
                  </NavButton>
                )}
                {analyticsTo && (
                  <NavButton
                    component={RouterLink}
                    to={analyticsTo}
                    className={activePath === analyticsTo ? 'active' : undefined}
                    startIcon={<InsightsIcon />}
                  >
                    Analytics
                  </NavButton>
                )}
                <NavButton
                  component={RouterLink}
                  to="/scratchpad"
                  className={activePath === '/scratchpad' ? 'active' : undefined}
                  startIcon={<FormatListBulletedIcon />}
                >
                  My Ideas
                </NavButton>
                {user?.isAdmin && (
                  <NavButton component={RouterLink} to="/admin" className={activePath === '/admin' ? 'active' : undefined}>
                    Admin
                  </NavButton>
                )}
              </>
            )}
          </Box>

          {/* Session Indicator */}
          {isAuthenticated && session && session.sessionId && (
            <Box sx={{ mx: 2, display: { xs: 'none', sm: 'block' } }}>
              <SessionIndicator
                icon={<BusinessIcon />}
                label={`${session.name || 'Active Session'} (${participant?.role || 'Participant'})`}
                component={RouterLink}
                to={`/meeting/${session.sessionId}`}
                clickable
                size="small"
              />
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {authLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : isAuthenticated ? (
              <>
                {/* Notifications */}
                <IconButton sx={{ display: { xs: 'none', sm: 'flex' }, color: '#a3a3a3', '&:hover': { color: '#e5e5e5' } }}>
                  <Badge badgeContent={0} color="primary">
                    <NotificationsIcon />
                  </Badge>
                </IconButton>

                {/* User Menu */}
                <IconButton onClick={handleMenuOpen}>
                  <Avatar 
                    sx={{ 
                      width: 36, 
                      height: 36, 
                      bgcolor: 'primary.main',
                      fontSize: '0.9rem',
                      fontWeight: 600
                    }}
                  >
                    {user?.name ? user.name.charAt(0).toUpperCase() : <AccountCircleIcon />}
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      '& .MuiMenuItem-root': {
                        px: 2,
                        py: 1,
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {user?.name || user?.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                  </Box>
                  {scrumEnabled && (
                    <MenuItem component={RouterLink} to="/dashboard" onClick={handleMenuClose}>
                      Dashboard
                    </MenuItem>
                  )}
                  {shapeUpEnabled && (
                    <MenuItem component={RouterLink} to="/shape-up" onClick={handleMenuClose}>
                      Shape Up
                    </MenuItem>
                  )}
                  {shapeUpEnabled && (
                    <MenuItem component={RouterLink} to="/cycles" onClick={handleMenuClose}>
                      Cycles
                    </MenuItem>
                  )}
                  {shapeUpEnabled && (
                    <MenuItem component={RouterLink} to="/bugs" onClick={handleMenuClose}>
                      Bugs
                    </MenuItem>
                  )}
                  {stakeholdersEnabled && (
                    <MenuItem component={RouterLink} to="/shape-up/stakeholders" onClick={handleMenuClose}>
                      Stakeholders
                    </MenuItem>
                  )}
                  {analyticsTo && (
                    <MenuItem component={RouterLink} to={analyticsTo} onClick={handleMenuClose}>
                      Analytics
                    </MenuItem>
                  )}
                  <MenuItem component={RouterLink} to="/scratchpad" onClick={handleMenuClose}>
                    My Ideas
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/organizations" onClick={handleMenuClose}>
                    Organizations
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/account" onClick={handleMenuClose}>
                    Account
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/settings" onClick={handleMenuClose}>
                    Settings
                  </MenuItem>
                  {session && session.sessionId && (
                    <MenuItem 
                      component={RouterLink} 
                      to={`/meeting/${session.sessionId}`} 
                      onClick={handleMenuClose}
                    >
                      Current Session
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NavButton component={RouterLink} to="/login">
                  Sign In
                </NavButton>
                <PrimaryButton component={RouterLink} to="/create-session">
                  Start Free Trial
                </PrimaryButton>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar; 